import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Creator, Approval, Followup, Partnership, Contract, Agent, ActivityEvent } from '@/types/entities';
import { Icon } from '@/components/ui/Icon';
import { getStatusMeta } from '@/config/statuses.config';
import { AI_PROVIDERS } from '@/config/aiProviders.config';
import { useI18n } from '@/i18n';

const TODAY = '2026-08-19'; // same fixed reference date FollowUpsPage uses — see Known Issues in the Phase C checkpoint
const DEFAULT_CONTRACT_EXPIRY_WARNING_DAYS = 30; // fallback until settings load; Settings → Contracts overrides this

function daysBetween(from: string, to: string): number {
  return Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000);
}

/**
 * Executive home for Ugarits — every card here either counts something
 * real from dataService, or links somewhere real. Nothing on this page
 * is invented: no revenue figures, no social metrics, no AI activity
 * that hasn't actually happened. Sections/priority order follow Phase C:
 * Needs Your Attention → Today → Business → AI Operations → Quick Actions.
 */
export function OverviewPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [connectors, setConnectors] = useState<{ key: string; displayName: string; status: string }[]>([]);
  const [systemServices, setSystemServices] = useState<{ key: string; displayName: string; status: string }[]>([]);
  const [contractExpiryWarningDays, setContractExpiryWarningDays] = useState(DEFAULT_CONTRACT_EXPIRY_WARNING_DAYS);

  useEffect(() => {
    dataService.listCreators().then(setCreators);
    dataService.listApprovals().then(setApprovals);
    dataService.listFollowups().then(setFollowups);
    dataService.listPartnerships().then(setPartnerships);
    dataService.listContracts().then(setContracts);
    dataService.listAgents().then(setAgents);
    dataService.listActivity().then(setActivity);
    dataService.listConnectorStatuses().then(setConnectors);
    dataService.listSystemServices().then(setSystemServices);
    dataService.getSettings().then((s) => setContractExpiryWarningDays(s.contractRules.expiryWarningDays));
  }, []);

  const pendingApprovals = useMemo(() => approvals.filter((a) => a.status === 'pending'), [approvals]);
  const overdueFollowups = useMemo(() => followups.filter((f) => f.status === 'open' && f.dueDate < TODAY), [followups]);
  const atRiskCreators = useMemo(() => creators.filter((c) => !c.archived && c.riskLevel === 'high'), [creators]);
  const expiringContracts = useMemo(
    () => contracts.filter((c) => !c.archived && c.expiryDate && daysBetween(TODAY, c.expiryDate) >= 0 && daysBetween(TODAY, c.expiryDate) <= contractExpiryWarningDays),
    [contracts, contractExpiryWarningDays]
  );
  const systemIssues = useMemo(() => systemServices.filter((s) => getStatusMeta('system', s.status).tone === 'crit' || getStatusMeta('system', s.status).tone === 'warn'), [systemServices]);
  const todaysFollowups = useMemo(() => followups.filter((f) => f.status === 'open' && f.dueDate === TODAY), [followups]);
  const recentActivity = useMemo(() => [...activity].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)).slice(0, 6), [activity]);
  const connectorIssues = useMemo(() => connectors.filter((c) => getStatusMeta('connector', c.status).tone === 'crit' || getStatusMeta('connector', c.status).tone === 'warn'), [connectors]);
  const configuredAgents = useMemo(() => agents.filter((a) => !a.archived && a.status !== 'waiting_credentials'), [agents]);
  const liveProviders = useMemo(() => Object.values(AI_PROVIDERS).filter((p) => p.configured), []);

  const attentionItems = [
    { key: 'approvals', label: t('overview.pendingApprovals'), count: pendingApprovals.length, route: '/approvals' },
    { key: 'overdue', label: t('overview.overdueFollowups'), count: overdueFollowups.length, route: '/followups' },
    { key: 'risk', label: t('overview.atRiskCreators'), count: atRiskCreators.length, route: '/creators' },
    { key: 'expiring', label: t('overview.expiringContracts'), count: expiringContracts.length, route: '/contracts' },
    { key: 'system', label: t('overview.systemIssues'), count: systemIssues.length, route: '/system' },
  ].filter((i) => i.count > 0);

  return (
    <div>
      <h1>{t('nav.overview')}</h1>

      <SectionLabel icon="alert" text={t('overview.needsAttention')} />
      {attentionItems.length === 0 ? (
        <div className="card overview-empty" style={{ padding: 16, marginBottom: 20 }}>{t('overview.nothingNeedsAttention')}</div>
      ) : (
        <div className="overview-grid" style={{ marginBottom: 20 }}>
          {attentionItems.map((i) => (
            <button key={i.key} className="overview-tile overview-tile-alert" onClick={() => navigate(i.route)}>
              <div className="overview-tile-count crit">{i.count}</div>
              <div className="overview-tile-label">{i.label}</div>
            </button>
          ))}
        </div>
      )}

      <SectionLabel text={t('overview.today')} />
      <div className="overview-two-col" style={{ marginBottom: 20 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{t('overview.todaysFollowups')}</div>
          {todaysFollowups.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('overview.noneToday')}</div>
          ) : (
            todaysFollowups.map((f) => (
              <div key={f.id} className="overview-row" onClick={() => navigate('/followups')}>
                <span>{f.entityLabel} — {f.title}</span>
                <span className={`badge ${f.priority === 'high' ? 'crit' : f.priority === 'medium' ? 'warn' : 'good'}`}>{f.priority}</span>
              </div>
            ))
          )}
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{t('overview.recentActivity')}</div>
          {recentActivity.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('overview.noRecentActivity')}</div>
          ) : (
            recentActivity.map((a) => (
              <div key={a.id} className="overview-row" onClick={() => navigate('/activity')}>
                <span>{a.actorLabel} — {a.action}</span>
                <span className={`badge ${a.result === 'crit' ? 'crit' : a.result === 'warn' ? 'warn' : a.result === 'good' ? 'good' : 'info'}`}>{a.result}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <SectionLabel text={t('overview.business')} />
      <div className="overview-grid" style={{ marginBottom: 20 }}>
        <button className="overview-tile" onClick={() => navigate('/creators')}>
          <div className="overview-tile-count">{creators.filter((c) => !c.archived).length}</div>
          <div className="overview-tile-label">{t('nav.creators')}</div>
        </button>
        <button className="overview-tile" onClick={() => navigate('/partnerships')}>
          <div className="overview-tile-count">{partnerships.filter((p) => !p.archived).length}</div>
          <div className="overview-tile-label">{t('nav.partnerships')}</div>
        </button>
        <button className="overview-tile" onClick={() => navigate('/contracts')}>
          <div className="overview-tile-count">{contracts.filter((c) => !c.archived).length}</div>
          <div className="overview-tile-label">{t('nav.contracts')}</div>
        </button>
      </div>

      <SectionLabel text={t('overview.aiOperations')} />
      <div className="overview-grid" style={{ marginBottom: 20 }}>
        <button className="overview-tile" onClick={() => navigate('/agents')}>
          <div className="overview-tile-count">{configuredAgents.length}/{agents.filter((a) => !a.archived).length}</div>
          <div className="overview-tile-label">{t('overview.agentsConfigured')}</div>
        </button>
        <button className="overview-tile" onClick={() => navigate('/approvals')}>
          <div className="overview-tile-count">{pendingApprovals.length}</div>
          <div className="overview-tile-label">{t('overview.approvalQueue')}</div>
        </button>
        <button className="overview-tile" onClick={() => navigate('/connectors')}>
          <div className={`overview-tile-count ${connectorIssues.length > 0 ? 'warn' : ''}`}>{connectorIssues.length}</div>
          <div className="overview-tile-label">{t('overview.connectorIssues')}</div>
        </button>
        <div className="overview-tile overview-tile-static">
          <div className="overview-tile-count">{liveProviders.length}/{Object.keys(AI_PROVIDERS).length}</div>
          <div className="overview-tile-label">{t('overview.aiProvidersLive')}</div>
        </div>
      </div>

      <SectionLabel icon="zap" text={t('overview.quickActions')} />
      <div className="overview-actions">
        <button className="btn-primary" onClick={() => navigate('/creators?new=1')}>{t('quickCreate.creator')}</button>
        <button className="btn-primary" onClick={() => navigate('/followups?new=1')}>{t('quickCreate.followup')}</button>
        <button className="btn-primary" onClick={() => navigate('/approvals')}>{t('overview.reviewApprovals')}</button>
        <button className="btn-primary" onClick={() => navigate('/partnerships?new=1')}>{t('quickCreate.partnership')}</button>
        <button className="btn-primary" onClick={() => navigate('/contracts?new=1')}>{t('quickCreate.contract')}</button>
        <button className="btn-primary" onClick={() => navigate('/agents?new=1')}>{t('quickCreate.agent')}</button>
        <button onClick={() => document.dispatchEvent(new CustomEvent('ugarits:open-ask-ai'))}>✦ {t('askAi.shortLabel')}</button>
      </div>
    </div>
  );
}

function SectionLabel({ text, icon }: { text: string; icon?: string }) {
  return (
    <div className="overview-section-label">
      {icon && <Icon name={icon} size={14} />} {text}
    </div>
  );
}
